/**
 * @fileoverview Productivity & Efficiency page — per-employee business, cost & profit metrics.
 * @description Ported from the InsightBox Banking design system's own
 * `ProductivityEfficiency/PerEmployeeMetrics.tsx`. Single-FY only (no "5 Years" trend mode — every
 * metric here is a per-employee ratio for one year), so this page's `FyFilterSelect` never resolves
 * to a multi-year chart.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  BranchFilterSelect,
  FIVE_YEAR_ID,
  FyFilterSelect,
  HorizontalValueBars,
  LastUpdatedLabel,
  branchLabel,
  formatCr,
  formatLacs,
  formatNumber,
  perEmployeeMetrics,
  resolveBankingAccessScope,
  resolveFy,
  useBankingData,
} from '@/features/banking'
import BankingKpiStrip from '@/shared/components/molecules/BankingKpiStrip'
import BusinessSummaryPanel from '@/shared/components/molecules/BusinessSummaryPanel'
import ChartCard from '@/shared/components/molecules/ChartCard'
import PageHeader from '@/shared/components/molecules/PageHeader'
import { CHART_ERROR, CHART_SUCCESS, CHART_WARNING } from '@/shared/constants/chart-theme.constants'

import type { FyFilter, HorizontalValueBarItem } from '@/features/banking'
import type { BankingKpiStripItem } from '@/shared/components/molecules/BankingKpiStrip'

/**
 * @component
 */
function BankingProductivityPage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const [fyFilter, setFyFilter] = useState<FyFilter>(FIVE_YEAR_ID)
  const bankingScope = resolveBankingAccessScope(useCurrentSession().data?.bankingScope)
  const [branchId, setBranchId] = useState(bankingScope.defaultBranchId)

  const header = (
    <PageHeader
      kicker={t('banking.productivity.kicker')}
      title={t('banking.productivity.title')}
      description={t('banking.productivity.description')}
      filters={
        <>
          <FyFilterSelect value={fyFilter} onChange={setFyFilter} />
          <BranchFilterSelect value={branchId} onChange={setBranchId} scope={bankingScope} />
          {dataQuery.data && <LastUpdatedLabel value={dataQuery.data.meta.lastUpdated} />}
        </>
      }
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <BankingKpiStrip
          loading
          items={[
            { label: '', value: '', highlight: true },
            { label: '', value: '' },
            { label: '', value: '' },
            { label: '', value: '' },
          ]}
        />
        <ChartCard title="" loading>
          <div />
        </ChartCard>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data
  const fy = resolveFy(fyFilter)
  const m = perEmployeeMetrics(data, fy, branchId)

  const costGrossNetBars: HorizontalValueBarItem[] = [
    {
      key: 'gross',
      label: t('banking.productivity.bars.grossProfit'),
      value: m.grossProfitPerEmployeeLacs,
      displayValue: formatLacs(m.grossProfitPerEmployeeLacs),
      color: CHART_WARNING,
    },
    {
      key: 'cost',
      label: t('banking.productivity.bars.employeeCost'),
      value: m.costPerEmployeeLacs,
      displayValue: formatLacs(m.costPerEmployeeLacs),
      color: CHART_ERROR,
    },
    {
      key: 'net',
      label: t('banking.productivity.bars.netProfit'),
      value: m.netProfitPerEmployeeLacs,
      displayValue: formatLacs(m.netProfitPerEmployeeLacs),
      color: CHART_SUCCESS,
    },
  ]

  const ratioStats = [
    {
      label: t('banking.productivity.ratios.grossProfitOverOwnFunds'),
      value: `${m.grossProfitOverOwnFundsPct.toFixed(2)}%`,
    },
    {
      label: t('banking.productivity.ratios.costOverWorkingCapital'),
      value: `${m.costOverWorkingCapitalPct.toFixed(2)}%`,
    },
    {
      label: t('banking.productivity.ratios.costOverIncome'),
      value: `${m.costOverIncomePct.toFixed(2)}%`,
    },
  ]

  const kpis: BankingKpiStripItem[] = [
    {
      label: t('banking.productivity.kpi.businessPerEmployee'),
      value: formatCr(m.businessPerEmployeeCr),
      sublabel: t('banking.productivity.kpi.employeeCount', {
        count: formatNumber(m.employees),
      }),
      highlight: true,
    },
    {
      label: t('banking.productivity.kpi.costPerEmployee'),
      value: formatLacs(m.costPerEmployeeLacs),
      sublabel: t('banking.productivity.kpi.employeeCost'),
    },
    {
      label: t('banking.productivity.kpi.netProfitPerEmployee'),
      value: formatLacs(m.netProfitPerEmployeeLacs),
      sublabel: t('banking.productivity.kpi.totalNetProfit', {
        value: formatCr(m.netProfitCr),
      }),
    },
    {
      label: t('banking.productivity.kpi.employeeCount2'),
      value: formatNumber(m.employees),
      sublabel: t('banking.productivity.kpi.referenceDenominator'),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      {header}
      <BankingKpiStrip items={kpis} />

      <ChartCard
        title={t('banking.productivity.chartTitle', { fy })}
        subtitle={t('banking.productivity.chartSubtitle', { branch: branchLabel(branchId) })}
      >
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(340px,100%),1fr))] gap-6">
          <div className="flex flex-col">
            <p className="mb-2 text-sm font-semibold text-primary">
              {t('banking.productivity.section1Title')}
            </p>
            <p className="mb-3 text-xs text-label">{t('banking.productivity.section1Subtitle')}</p>
            <div className="flex flex-1 flex-col items-center justify-center rounded-xl bg-input-bg px-6 py-5 text-center">
              <p className="text-4xl font-extrabold text-accent">
                {formatCr(m.businessPerEmployeeCr)}
              </p>
              <p className="mt-1 text-sm text-label">{t('banking.productivity.perEmployee')}</p>
            </div>
          </div>
          <div>
            <p className="mb-3 text-sm font-semibold text-primary">
              {t('banking.productivity.section2Title')}
            </p>
            <p className="mb-3 text-xs text-label">{t('banking.productivity.section2Subtitle')}</p>
            <HorizontalValueBars
              items={costGrossNetBars}
              axisLabel={t('banking.productivity.lakhsAxisLabel')}
              labelWidth={110}
              height={210}
            />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(min(200px,100%),1fr))] gap-4 border-t border-border pt-4">
          {ratioStats.map((stat) => (
            <div
              key={stat.label}
              className="flex items-center gap-3 rounded-xl bg-input-bg px-4 py-3"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-light text-primary">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="19" y1="5" x2="5" y2="19" />
                  <circle cx="6.5" cy="6.5" r="2.5" />
                  <circle cx="17.5" cy="17.5" r="2.5" />
                </svg>
              </span>
              <div>
                <p className="text-[11px] font-semibold tracking-wide text-label uppercase">
                  {stat.label}
                </p>
                <p className="text-base font-bold text-text">{stat.value}</p>
              </div>
            </div>
          ))}
        </div>
      </ChartCard>

      <BusinessSummaryPanel
        title={t('banking.businessSummary')}
        lines={[
          t('banking.productivity.summary.line1', {
            business: formatCr(m.businessPerEmployeeCr),
            cost: formatLacs(m.costPerEmployeeLacs),
            netProfit: formatLacs(m.netProfitPerEmployeeLacs),
          }),
          t('banking.productivity.summary.line2', {
            grossOverFunds: m.grossProfitOverOwnFundsPct.toFixed(2),
            costOverIncome: m.costOverIncomePct.toFixed(2),
          }),
        ]}
      />
    </div>
  )
}

export default BankingProductivityPage
